import React, {useEffect, useState} from 'react';
import axios from 'axios';
import MasterSelection from './MasterSelection';

const API = process.env.REACT_APP_API_BASE_URL;

const formatDateTime = value => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Lets a quotation pick a visitor (via the MasterSelection modal) and one or
 * more of their visits. On selection the backend marks those visits' lead
 * status ("Quotation Issued" on create, "Quotation Revision" on update).
 *
 * Props:
 *  - companyId: company to scope visitors to
 *  - visitor: selected visitor id (string) or null
 *  - visits: array of selected visit ids
 *  - onChange: (visitorId, visitIds) => void
 */
const VisitorVisitSelector = ({companyId, visitor, visits = [], onChange}) => {
  const [visitorName, setVisitorName] = useState('');
  const [showVisitorModal, setShowVisitorModal] = useState(false);
  const [visitorVisits, setVisitorVisits] = useState([]);
  const [loading, setLoading] = useState(false);

  // Resolve the selected visitor's name for display.
  useEffect(() => {
    if (!visitor) {
      setVisitorName('');
      return undefined;
    }
    let active = true;
    axios
      .get(`${API}/visitors/${visitor}`)
      .then(res => {
        if (active) setVisitorName(res.data?.data?.name || '');
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [visitor]);

  // Load the visitor's visits whenever the selected visitor changes.
  useEffect(() => {
    if (!visitor) {
      setVisitorVisits([]);
      return undefined;
    }
    let active = true;
    setLoading(true);
    axios
      .get(`${API}/visits/visitor/${visitor}`)
      .then(res => {
        if (active) setVisitorVisits(res.data?.data || []);
      })
      .catch(err => console.error('Error loading visits:', err))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [visitor]);

  const toggleVisit = visitId => {
    const next = visits.includes(visitId)
      ? visits.filter(id => id !== visitId)
      : [...visits, visitId];
    onChange(visitor, next);
  };

  return (
    <div className="space-y-3">
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">
          Visitor (links visits to this quotation)
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setShowVisitorModal(true)}
            className="flex-1 flex items-center justify-between border border-gray-300 rounded-lg px-3 py-2 text-sm bg-white hover:border-indigo-500">
            <span className={visitor ? 'text-gray-900' : 'text-gray-400'}>
              {visitorName || (visitor ? 'Selected visitor' : 'Select visitor')}
            </span>
            <span className="text-gray-400">▾</span>
          </button>
          {visitor ? (
            <button
              type="button"
              onClick={() => {
                setVisitorName('');
                onChange(null, []);
              }}
              className="px-3 rounded-lg border border-gray-300 text-gray-500 hover:text-red-500 text-sm">
              Clear
            </button>
          ) : null}
        </div>
      </div>

      {visitor ? (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Select Visits ({visits.length} selected)
          </label>
          {loading ? (
            <p className="text-sm text-gray-400">Loading visits...</p>
          ) : visitorVisits.length === 0 ? (
            <p className="text-sm text-gray-400">
              No visits recorded for this visitor.
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100">
              {visitorVisits.map(visit => (
                <label
                  key={visit._id}
                  className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-gray-50">
                  <input
                    type="checkbox"
                    checked={visits.includes(visit._id)}
                    onChange={() => toggleVisit(visit._id)}
                  />
                  <span className="flex-1">
                    {formatDateTime(visit.visitedAt)} ·{' '}
                    <span className="font-medium">{visit.response}</span> ·{' '}
                    {visit.leadStatus}
                    {visit.itemsOfInterest?.length
                      ? ` · ${visit.itemsOfInterest.join(', ')}`
                      : ''}
                  </span>
                </label>
              ))}
            </div>
          )}
        </div>
      ) : null}

      <MasterSelection
        isOpen={showVisitorModal}
        onClose={() => setShowVisitorModal(false)}
        onSelect={item => {
          setVisitorName(item.name || '');
          onChange(item._id, []);
          setShowVisitorModal(false);
        }}
        allowedTypes={['Visitor']}
        defaultType="Visitor"
        title="Select Visitor"
      />
    </div>
  );
};

export default VisitorVisitSelector;
